import { APIService, APIError } from '../../../lib/api/APIService';

// Auth API class extending the base APIService
class AuthAPI extends APIService {
  async login(email, password, handoffToken = null) {
    const body = { email, password };
    if (handoffToken) body.handoff_token = handoffToken;
    return this.post('/api/auth/login', body, { skipAuth: true });
  }

  async register(name, email, password, meta = {}, handoffToken = null) {
    const body = { name, email, password, ...meta };
    if (handoffToken) body.handoff_token = handoffToken;
    return this.post('/api/auth/register', body, { skipAuth: true });
  }

  async logout() {
    try {
      const result = await this.post('/api/auth/logout');
      // Clear tokens after successful logout
      this.clearTokens();
      return result;
    } catch (error) {
      // Clear tokens even if backend logout fails
      this.clearTokens();
      throw error;
    }
  }

  async logoutAll() {
    try {
      const result = await this.post('/api/auth/logout-all');
      this.clearTokens();
      return result;
    } catch (error) {
      this.clearTokens();
      throw error;
    }
  }

  async refreshToken(refreshToken) {
    return this.post('/api/auth/refresh', 
      { refresh_token: refreshToken }, 
      { skipAuth: true, isRefreshRequest: true }
    );
  }

  async getCurrentUser() {
    return this.get('/api/auth/me');
  }

  async getProfile() {
    return this.get('/api/auth/profile');
  }

  async updateProfile(data) {
    return this.put('/api/auth/profile', data);
  }

  async forgotPassword(email) {
    return this.post('/api/auth/forgot-password', { email }, { skipAuth: true, timeout: 30000 });
  }

  async resetPassword(token, password) {
    return this.post('/api/auth/reset-password', { token, password }, { skipAuth: true, timeout: 30000 });
  }

  async setPassword(password, confirmPassword) {
    return this.post('/api/auth/set-password', {
      password,
      confirm_password: confirmPassword,
    });
  }

  async sendMagicLink(email, handoffToken = null) {
    const body = { email };
    if (handoffToken) body.handoff_token = handoffToken;
    return this.post('/api/auth/magic-link', body, { skipAuth: true, timeout: 30000 });
  }

  async magicLogin(token) {
    // No handoffToken parameter here by design — the handoff (if any) is
    // resolved server-side from the verified magic-link JWT's embedded
    // internal handoff_id, never from a client-supplied field on this call.
    return this.post('/api/auth/magic-login', { token }, { skipAuth: true });
  }

  async linkedinLogin(code, redirectUri, handoffToken = null) {
    const body = { code, redirect_uri: redirectUri };
    if (handoffToken) body.handoff_token = handoffToken;
    return this.post('/api/auth/linkedin', body, { skipAuth: true });
  }

  async googleLogin(code, redirectUri, handoffToken = null) {
    const body = { code, redirect_uri: redirectUri };
    if (handoffToken) body.handoff_token = handoffToken;
    return this.post('/api/auth/google', body, { skipAuth: true });
  }
}

// Create and export auth API instance
export const authAPI = new AuthAPI();

// Export utility functions
export const utils = {
  // Validate email address format
  isValidEmail: (email) => {
    return typeof email === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  },

  // Check if error is network-related
  isNetworkError: (error) => {
    return error.name === 'TypeError' || error.name === 'AbortError' || 
           error.message.includes('fetch') || error.message.includes('network');
  },

  // Check if error is auth-related
  isAuthError: (error) => {
    return error.status === 401 || error.status === 403;
  },

  // Check if error requires login redirect
  requiresLogin: (error) => {
    return error.status === 401 || (error.data && error.data.requiresLogin);
  },

  // Format error message for user display
  formatErrorMessage: (error) => {
    if (error instanceof APIError) {
      // Handle specific backend error messages
      if (error.message.includes('Email already registered')) {
        return 'This email is already registered. Please try logging in instead.';
      }
      if (error.message.includes('Email not found')) {
        return 'No account found with this email address.';
      }
      if (error.message.includes('Incorrect password')) {
        return 'Incorrect password. Please try again.';
      }
      if (error.message.includes('Token expired')) {
        return 'Your session has expired. Please log in again.';
      }
      
      return error.message;
    }
    
    if (error.name === 'AbortError') {
      return 'Request timeout. Please check your connection and try again.';
    }
    
    if (utils.isNetworkError(error)) {
      return 'Network error. Please check your internet connection.';
    }
    
    return 'Something went wrong. Please try again.';
  },
};